import { create } from 'zustand';

// Internal buffers for batching
let chainBuffer = {};
let futuresBuffer = {};
let batchTimer = null;

/**
 * syncOptionChainStore
 * --------------------
 * Mirrors optionChainStore but only holds is_sync: true data.
 * Used exclusively by SyncArbitrageView.
 */
export const useSyncOptionChainStore = create((set, get) => ({
    chainData: {},
    futuresData: {},
    status: 'disconnected',

    setStatus: (status) => set({ status }),

    _flushBatch: () => {
        set((state) => {
            const newChainData = { ...state.chainData };
            const newFuturesData = { ...state.futuresData };

            Object.entries(chainBuffer).forEach(([underlying, expiries]) => {
                newChainData[underlying] = { ...newChainData[underlying] };
                Object.entries(expiries).forEach(([expiry, strikes]) => {
                    newChainData[underlying][expiry] = { ...newChainData[underlying][expiry] };
                    Object.entries(strikes).forEach(([strike, exchanges]) => {
                        newChainData[underlying][expiry][strike] = { ...newChainData[underlying][expiry][strike] };
                        Object.entries(exchanges).forEach(([exchange, data]) => {
                            newChainData[underlying][expiry][strike][exchange] = {
                                ...newChainData[underlying][expiry][strike][exchange],
                                ...data
                            };
                        });
                    });
                });
            });

            Object.entries(futuresBuffer).forEach(([key, data]) => {
                newFuturesData[key] = { ...newFuturesData[key], ...data };
            });

            chainBuffer = {};
            futuresBuffer = {};
            batchTimer = null;

            return { chainData: newChainData, futuresData: newFuturesData };
        });
    },

    updateOptionChain: (data) => {
        if (Array.isArray(data)) {
            data.forEach(item => get()._bufferOptionUpdate(item));
        } else {
            get()._bufferOptionUpdate(data);
        }
    },

    _bufferOptionUpdate: (data) => {
        // Only accept is_sync MCX ticks OR any Comex/Nymex tick
        if (!data.is_sync && data.exchange !== 'COMEX' && data.exchange !== 'NYMEX') return;
        const { underlying, expiry, strike, exchange, type, ltp, ltt, ltp_change, is_mid } = data;
        if (!underlying || !expiry || !strike) return;
        const exchangeName = exchange || 'MCX';

        if (!chainBuffer[underlying]) chainBuffer[underlying] = {};
        if (!chainBuffer[underlying][expiry]) chainBuffer[underlying][expiry] = {};
        if (!chainBuffer[underlying][expiry][strike]) chainBuffer[underlying][expiry][strike] = {};
        if (!chainBuffer[underlying][expiry][strike][exchangeName]) chainBuffer[underlying][expiry][strike][exchangeName] = {};

        const side = type.toLowerCase();
        chainBuffer[underlying][expiry][strike][exchangeName][side] = { ltp, ltt, ltp_change, is_mid };

        if (!batchTimer) {
            batchTimer = setTimeout(() => get()._flushBatch(), 50);
        }
    },

    updateFutures: (data) => {
        if (Array.isArray(data)) {
            data.forEach(item => get()._bufferFuturesUpdate(item));
        } else {
            get()._bufferFuturesUpdate(data);
        }
    },

    _bufferFuturesUpdate: (data) => {
        const { exchange, ltp, symbol, expiry, underlying } = data;
        const key = `${exchange}_${underlying || 'GOLD'}_${expiry}`;
        futuresBuffer[key] = { exchange, ltp, symbol, expiry, underlying: underlying || 'GOLD' };
        if (!batchTimer) {
            batchTimer = setTimeout(() => get()._flushBatch(), 50);
        }
    },

    deleteOption: (data) => {
        // NO-OP: Sync view handles its own visibility via derived mapping.
        // We ignore DELETE to prevent flickering when backend resubscribes.
        return;
    },

    reset: () => {
        chainBuffer = {};
        futuresBuffer = {};
        if (batchTimer) clearTimeout(batchTimer);
        batchTimer = null;
        set({ chainData: {}, futuresData: {} });
    }
}));
